import React from 'react';
import { arrayOf, bool, func, node, oneOf, oneOfType, shape, string } from 'prop-types';
import { cx } from '@emotion/css';

import IconClose from '@/assets/Icon/svg/Close';
import { Row, Col } from '@/components/Layout';

import {
  getAlertStyles,
  styMessageBox,
  styBtnClose,
  styTitle,
  styDescription,
  styActionBtn,
} from './style';
import { MESSAGE_TYPES } from './constants';

const MessageBox = (props) => {
  const {
    actions,
    children,
    className,
    icon,
    onClose,
    title,
    text,
    textColor,
    type,
    withArrowTip,
    ...rest
  } = props;

  const styProps = { type, icon, withArrowTip };

  const renderIcon = () => {
    if (icon === true) {
      // If props.icon is true, use default icon
      const Icon = getAlertStyles(type).icon;
      return (
        <span>
          <Icon color={getAlertStyles(type).iconColor} size={20} />
        </span>
      );
    }
    if (React.isValidElement(icon)) {
      // If icon is an element, use icon as custom icon
      return (
        <span>
          <icon.type {...icon.props} color={getAlertStyles(type).iconColor} size={20} />
        </span>
      );
    }
    return null;
  };

  return (
    <div className={cx(styMessageBox(styProps), className)} {...rest}>
      <Row noGutters>
        {renderIcon()}
        <Col>
          {title && (
            <p className={styTitle(styProps)} data-testid="title">
              {title}
            </p>
          )}
          {text && (
            <p className={styDescription(styProps)} data-testid="text">
              {text}
            </p>
          )}
          {children && (
            <div className={styDescription(styProps)} data-testid="children">
              {children}
            </div>
          )}
        </Col>
        {onClose && (
          <button
            type="button"
            className={styBtnClose(styProps)}
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onClose(e);
              }
            }}
            aria-label="close"
          >
            <IconClose size={12} color={getAlertStyles(type).iconColor} />
          </button>
        )}
      </Row>

      <Row noGutters>
        <Col size={12}>
          {actions.length > 0 && (
            <Row align="left" mt={12}>
              {actions.map((act, i) => (
                <Col key={act.text} size="auto" data-testid={`action_${i}`}>
                  <button
                    type="button"
                    className={styActionBtn(styProps)}
                    onClick={act.onClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        act.onClick(e);
                      }
                    }}
                  >
                    {act.text}
                  </button>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

MessageBox.propTypes = {
  actions: arrayOf(
    shape({
      text: string,
      onClick: func,
    })
  ),
  children: node,
  className: string,
  icon: oneOfType([bool, node]),
  onClose: func,
  title: string,
  text: string,
  type: oneOf(MESSAGE_TYPES),
  textColor: string,
  withArrowTip: bool,
};

MessageBox.defaultProps = {
  actions: [],
  children: null,
  className: '',
  icon: true,
  onClose: null,
  title: '',
  text: '',
  type: 'info',
  textColor: null,
  withArrowTip: false,
};

export default MessageBox;
